var __spreadArray = this && this.__spreadArray || function (to, from, pack) {
  if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
    if (ar || !(i in from)) {
      if (!ar) ar = Array.prototype.slice.call(from, 0, i);
      ar[i] = from[i];
    }
  }
  return to.concat(ar || Array.prototype.slice.call(from));
};
import { HttpStatusCode } from 'axios';
import { capitalize } from 'lodash';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { generatePath, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { StatusPermission, lowestPermission } from '../../enum';
import { FULL_REPORT_ROUTER_URL } from '../../router';
import { useAPI, useAuthenticatedAPI } from '../../state';
import { useExistMultiskillReport } from '../annual/selectors';
import { useUserPermissionsAPI, useUserSessionAPI } from '../app/selectors';
import { useExistMultiskillPeriodReport } from '../period/selectors';
import { selectPlanType } from '../wizard/selectors';
import { ActionType, FILE_EXT, FILE_NAME_BY_REPORT, FILE_NAME_BY_TAB } from './constants';
import { useReportDownload, useReportType, useSelectedTab } from './hooks';
import { DisplayMode } from './resourcing';
var PLAN_API = '/api/plan/report/{basePlanId}/{id}';
var SCENARIO_API = '/api/plan/scenario/{id}';
var ADD_SCENARIO = '/api/plan/scenario/{basePlanId}';
var SWITCH_SCENARIO = '/api/plan/switchScenario/{id}';
var EXPORT_API = '/api/report/{reportType}/{tab}/{action}?basePlanId={basePlanId}&displayMode={displayMode}';
var CANCEL_SIMULATION = '/api/report/simulation/cancel-simulation/{basePlanId}/{id}';
export var useReportAPI = function () {
  var _a = useParams(),
    basePlanId = _a.basePlanId,
    id = _a.id;
  return useAPI(PLAN_API, {
    basePlanId: basePlanId,
    id: id
  });
};
/**
 * Downgrades an org-wide permission (capacity, financial, actual, recruitment) by the open
 * report plan's client/LOB permission (lowest wins). A view-only plan is read-only in every
 * regard, regardless of the caller's org-wide permission for that feature.
 */
export var useReportEffectivePlanPermission = function (orgPermission) {
  var _a;
  var data = useReportAPI()[0].data;
  return lowestPermission(orgPermission !== null && orgPermission !== void 0 ? orgPermission : StatusPermission.Edit, (_a = data === null || data === void 0 ? void 0 : data.effectivePermission) !== null && _a !== void 0 ? _a : StatusPermission.Edit);
};
/**
 * Capacity permission for the open report plan: the user's org-wide capacity permission
 * downgraded by the plan's client/LOB permission (lowest wins). Use this instead of
 * `useUserPermissionsAPI().capacityPermission` wherever a specific report plan is open.
 */
export var useReportEffectivePermission = function () {
  var capacityPermission = useUserPermissionsAPI().capacityPermission;
  return useReportEffectivePlanPermission(capacityPermission);
};
export var useReportPlanType = function () {
  var data = useReportAPI()[0].data;
  return selectPlanType(data);
};
/** `basePlanId` is the family's key after the delete — it moves when the deleted row was that key. */
export var useDeleteScenario = function (id) {
  return useAuthenticatedAPI(SCENARIO_API, {
    id: id
  }, 'DELETE');
};
export var useAddScenarioAPI = function () {
  var basePlanId = useParams().basePlanId;
  return useAuthenticatedAPI(ADD_SCENARIO, {
    basePlanId: basePlanId
  }, 'POST');
};
export var useRenameScenario = function (id) {
  return useAPI(SCENARIO_API, {
    id: id
  }, 'PUT');
};
export var useSwitchScenario = function (id) {
  return useAuthenticatedAPI(SWITCH_SCENARIO, {
    id: id
  }, 'PUT');
};
export var useExistMultiskillMode = function () {
  var existMultiskillReport = useExistMultiskillReport();
  var existMultiskillPeriodReport = useExistMultiskillPeriodReport();
  return existMultiskillReport || existMultiskillPeriodReport;
};
export var useDisplayModeParams = function () {
  var _a;
  var _b = useSearchParams(),
    searchParams = _b[0],
    setSearchParams = _b[1];
  var existMultiskillMode = useExistMultiskillMode();
  var displayMode = existMultiskillMode ? (_a = searchParams.get('displayMode')) !== null && _a !== void 0 ? _a : DisplayMode.All : DisplayMode.Single;
  var setDisplayMode = function (mode) {
    searchParams.delete('viewType');
    searchParams.set('displayMode', "".concat(mode));
    setSearchParams(searchParams, {
      replace: true
    });
  };
  return [displayMode, setDisplayMode];
};
var useFileName = function (action) {
  var _a, _b;
  var report = useReportType();
  var planType = useReportPlanType();
  var tab = useSelectedTab();
  var displayMode = useDisplayModeParams()[0];
  var reportName = (_b = (_a = FILE_NAME_BY_REPORT[report]) === null || _a === void 0 ? void 0 : _a[planType]) !== null && _b !== void 0 ? _b : planType;
  var tabName = FILE_NAME_BY_TAB[tab];
  var t = useTranslation(['report']).t;
  var tReportName = t(reportName, {
    defaultValue: reportName
  }).split(' ').map(capitalize).join('_');
  var tTabName = t(tabName, {
    defaultValue: tabName
  });
  var tDisplayMode = t(displayMode, {
    defaultValue: displayMode
  });
  var suffix = tDisplayMode.split('_').map(capitalize).join('_');
  return "".concat(tReportName, "_").concat(tTabName, "_").concat(suffix, ".").concat(FILE_EXT[action]);
};
export var useExportAPI = function (action, disabled) {
  if (action === void 0) {
    action = ActionType.Export;
  }
  var basePlanId = useParams().basePlanId;
  var tab = useSelectedTab();
  var reportType = useReportType();
  var fileName = useFileName(action);
  var displayMode = useDisplayModeParams()[0];
  var t = useTranslation(['report']).t;
  var tPrefix = t('forecast', {
    defaultValue: 'Forecast'
  });
  return useReportDownload(action, EXPORT_API, "".concat(tPrefix, "_").concat(fileName), {
    disabled: disabled,
    substitutions: {
      basePlanId: basePlanId,
      reportType: reportType,
      tab: tab,
      action: action,
      displayMode: displayMode
    }
  });
};
export var usePrintTableAPI = function () {
  return useExportAPI(ActionType.Print);
};
export var useCancelSimulation = function (id, basePlanId) {
  return useAPI(CANCEL_SIMULATION, {
    basePlanId: basePlanId,
    id: id
  }, 'POST');
};
export var useForbiddenRedirect = function (statusCodes) {
  var navigate = useNavigate();
  var _a = useUserSessionAPI(),
    fetchUserSession = _a[1];
  var reportData = useReportAPI()[0].data;
  useEffect(function () {
    if (statusCodes.includes(HttpStatusCode.Forbidden)) {
      fetchUserSession();
      navigate(generatePath(FULL_REPORT_ROUTER_URL.Annual, {
        basePlanId: reportData === null || reportData === void 0 ? void 0 : reportData.basePlanId
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, __spreadArray([], statusCodes, true));
};